import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-routing-machine/dist/leaflet-routing-machine.css";
import "leaflet-routing-machine";
import { Typography, Grid, Button } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import RouteIcon from "@mui/icons-material/Route";
import DirectionsIcon from "@mui/icons-material/Directions";

// Default Destination (Fallback)
const DEFAULT_DESTINATION = { lat: 12.881276, lng: 79.911454 };

// Create a Custom Glowing Marker
const createGlowingMarker = (color) =>
    L.divIcon({
        className: "custom-marker",
        html: `<div style="width: 15px; height: 15px; background-color: ${color}; border-radius: 50%; box-shadow: 0 0 10px ${color};"></div>`,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
        popupAnchor: [0, -10],
    });

// Format Data for Display
const getValidData = (primary, fallback) => {
    return primary !== 0 && primary !== null && primary !== undefined
        ? primary
        : fallback !== 0 && fallback !== null && fallback !== undefined
            ? fallback
            : "N/A";
};

const formatData = (value, suffix = "") => {
    return value === "N/A" ? value : `${value} ${suffix}`;
};

// Fix "Invalid Date" Issue in Last Reported Time
const formatDate = (date) => {
    if (!date || isNaN(Date.parse(date))) return "N/A";
    return new Date(date).toUTCString();
};

// Calculate Distance Between Two Points (in meters)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return Infinity;

    const R = 6371e3; // Radius of Earth in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
        Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
};

// Determine Stack Level from Altitude
const getStackLevel = (altitude) => {
    if (!altitude || altitude < 6) return "Ground Level";
    const levels = Math.floor(altitude / 6.096);
    if (levels === 1) return "First Stack Level";
    if (levels === 2) return "Second Stack Level";
    if (levels === 3) return "Third Stack Level";
    return `${levels}th Stack Level`;
};

// Routing Component for Directions
const RoutingComponent = ({ userLocation, destination }) => {
    const map = useMap();
    const routingControlRef = useRef(null);

    useEffect(() => {
        if (!userLocation || !destination) return;

        if (routingControlRef.current) {
            map.removeControl(routingControlRef.current);
        }

        routingControlRef.current = L.Routing.control({
            waypoints: [
                L.latLng(userLocation.lat, userLocation.lng),
                L.latLng(destination.lat, destination.lng),
            ],
            lineOptions: {
                styles: [{ color: 'green', opacity: 1, weight: 3 }]
            },
            routeWhileDragging: true,
            createMarker: () => null,
            addWaypoints: false,
            draggableWaypoints: false,
            show: true,
            router: L.Routing.osrmv1({
                serviceUrl: "https://osrm.trackabox.in/route/v1"
                // serviceUrl: "http://localhost:5000/route/v1"
            }),
        }).addTo(map);
        routingControlRef.current
            .on("routesfound", function (e) {
                console.log("✅ Route found!", e.routes[0]);
            })
            .on("routingerror", function (err) {
                console.error("❌ Routing error:", err);
            })
            .on("waypointschanged", function (e) {
                console.log("📍 Waypoints changed:", e.waypoints);
            });


        // routingControlRef.current = L.Routing.control({
        //     waypoints: [
        //         L.latLng(userLocation.lat, userLocation.lng),
        //         L.latLng(destination.lat, destination.lng),
        //     ],
        //     routeWhileDragging: true,
        //     createMarker: () => null,
        //     addWaypoints: false,
        //     draggableWaypoints: false,
        //     show: true,
        //     // 💥 Add your local OSRM server
        //     router: L.Routing.osrmv1({
        //         serviceUrl: "http://192.168.1.60:5000/route/v1" // <--- Use your own local server
        //     }),
        // }).addTo(map);

        return () => {
            if (routingControlRef.current) {
                map.removeControl(routingControlRef.current);
                routingControlRef.current = null;
            }
        };
    }, [map, userLocation, destination]);

    return null;
};


const Navigation = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [userLocation, setUserLocation] = useState(null);
    const [reachedDestination, setReachedDestination] = useState(false);
    const userMarkerRef = useRef(null);
    const destination = location.state?.destination || DEFAULT_DESTINATION;

    useEffect(() => {
        return () => {
            navigate("/gps-map", { replace: true });
        };
    }, [navigate]);

    useEffect(() => {
        if ("geolocation" in navigator) {
            navigator.geolocation.watchPosition(
                (position) => {
                    const newLocation = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };
                    console.log("📍 User location:", newLocation);
                    // setUserLocation(newLocation);
                    setUserLocation(newLocation);

                    if (userMarkerRef.current) {
                        userMarkerRef.current.setLatLng(newLocation);
                    }
                },
                (error) => {
                    console.error("Location Error:", error);
                    alert("Location access is required to generate the route.");
                },
                { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
            );
        }
    }, []);

    useEffect(() => {
        if ("geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setUserLocation({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    });
                },
                (error) => {
                    console.warn("GPS blocked. Using fallback location.");
                    setUserLocation({
                        lat: 13.0827, // Chennai or your demo spot
                        lng: 80.2707
                    });
                },
                { enableHighAccuracy: true, timeout: 10000 }
            );
        } else {
            setUserLocation({ lat: 13.0827, lng: 80.2707 });
        }
    }, []);


    useEffect(() => {
        if (userLocation && destination.lat && destination.lng) {
            const distance = calculateDistance(
                userLocation.lat,
                userLocation.lng,
                destination.lat,
                destination.lng
            );

            if (distance <= 50 && !reachedDestination) {
                setReachedDestination(true);
                if (userMarkerRef.current) {
                    userMarkerRef.current.openPopup(); // Auto-popup when destination is reached
                }
            }
        }
    }, [userLocation, destination, reachedDestination]);

    return (
        // <div key={destination.deviceCode} style={{ width: "100%", height: "95vh" }}>
        //     <MapContainer center={userLocation || destination} zoom={15} scrollWheelZoom={true} style={{ width: "100%", height: "100%" }}>
        //         <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <div key={destination.deviceCode || Math.random()} style={{ width: "100%", height: "95vh" }}>
            <MapContainer
                center={userLocation || destination}
                zoom={15}
                maxZoom={18}
                scrollWheelZoom={true}
                style={{ width: "100%", height: "100%" }}
            // >
            >
                {/* <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" /> */}
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {/* User's Real-Time Location with Color Change on Destination Met */}
                {userLocation && (
                    <Marker
                        ref={userMarkerRef}
                        position={[userLocation.lat, userLocation.lng]}
                        icon={createGlowingMarker(reachedDestination ? "green" : "blue")}
                    >
                        <Popup>{reachedDestination ? "🚀 You have reached your destination!" : "You are here"}</Popup>
                    </Marker>
                )}

                {/* Destination Marker with Complete Device Info */}
                <Marker position={[destination.lat, destination.lng]} icon={createGlowingMarker("red")}>
                    <Popup autoOpen>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#1976d2" }}>
                            📡 {getValidData(destination.name)}
                        </Typography>

                        <Grid container spacing={1} alignItems="center">
                            <Grid item xs={12}>
                                <Typography variant="caption">
                                    <RouteIcon fontSize="small" /> Device Code: {getValidData(destination.deviceCode)}
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="caption" sx={{ color: destination.insideGeofence ? "green" : "red" }}>
                                    <AssignmentTurnedInIcon fontSize="small" />
                                    {destination.insideGeofence ? "Inside Geofence ✅" : "Outside Geofence ❌"}
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="caption">
                                    <BatteryFullIcon fontSize="small" />
                                    Battery: {formatData(getValidData(destination.batteryPercentage), "%")}

                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="caption">
                                    ⚡ Shock: {getValidData(destination.shock)}
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="caption">
                                    🚀 Speed: {formatData(getValidData(destination.speed), 'km/h')}
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="caption">
                                    ⛰️ Stack Position: {getStackLevel(getValidData(destination.alt))} with {formatData((destination.alt), "m")}
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="caption">
                                    <LocationOnIcon fontSize="small" /> Coordinates:
                                    {getValidData(destination.lat)}, {getValidData(destination.lng)}
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="caption">
                                    <AccessTimeIcon fontSize="small" /> Last Reported: {formatDate(destination.lastReported)}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Popup>
                </Marker>

                {/* Routing Component */}
                {userLocation && <RoutingComponent userLocation={userLocation} destination={destination} />}
            </MapContainer>
        </div>
    );
};

export default Navigation;

